import React, { useEffect, useState } from 'react';
import {
  getUsers,
  createUser,
  updateUser,
  toggleUserActive,
} from '../api/adminApi';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import Modal from '../components/ui/Modal';
import Input from '../components/ui/Input';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { Plus, Edit2, Power, Save, X, Users } from 'lucide-react';

const styles = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 'var(--space-xl)',
    flexWrap: 'wrap',
    gap: 'var(--space-md)',
  },
  title: {
    fontFamily: 'var(--font-display)',
    fontSize: '1.75rem',
    fontWeight: 700,
    color: 'var(--color-text-primary)',
    margin: 0,
    letterSpacing: '0.03em',
  },
  subtitle: {
    fontSize: '0.9rem',
    color: 'var(--color-text-muted)',
    margin: '4px 0 0',
  },
  tableWrapper: {
    overflowX: 'auto',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid rgba(212, 175, 55, 0.1)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontFamily: 'var(--font-body)',
    fontSize: '0.9rem',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontFamily: 'var(--font-display)',
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'var(--color-text-muted)',
    backgroundColor: 'var(--color-bg-secondary)',
    borderBottom: '1px solid rgba(212, 175, 55, 0.1)',
  },
  td: {
    padding: '12px 16px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.03)',
    color: 'var(--color-text-secondary)',
  },
  row: {
    transition: 'background-color var(--transition-fast)',
  },
  empty: {
    textAlign: 'center',
    padding: 'var(--space-2xl)',
    color: 'var(--color-text-muted)',
  },
  centered: {
    display: 'flex',
    justifyContent: 'center',
    padding: 'var(--space-2xl)',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-md)',
  },
  formActions: {
    display: 'flex',
    gap: 'var(--space-sm)',
    justifyContent: 'flex-end',
    marginTop: 'var(--space-sm)',
  },
  error: {
    padding: 'var(--space-sm) var(--space-md)',
    backgroundColor: 'rgba(231, 76, 60, 0.1)',
    border: '1px solid rgba(231, 76, 60, 0.2)',
    borderRadius: 'var(--radius-md)',
    color: 'var(--color-danger)',
    fontSize: '0.85rem',
    marginBottom: 'var(--space-md)',
  },
  fieldLabel: {
    fontFamily: 'var(--font-display)',
    fontSize: '0.85rem',
    fontWeight: 500,
    color: 'var(--color-text-secondary)',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    marginBottom: 'var(--space-xs)',
    display: 'block',
  },
  select: {
    width: '100%',
    padding: '12px 16px',
    backgroundColor: 'var(--color-bg-secondary)',
    border: '2px solid rgba(212, 175, 55, 0.15)',
    borderRadius: 'var(--radius-md)',
    color: 'var(--color-text-primary)',
    fontSize: '1rem',
    fontFamily: 'var(--font-body)',
    outline: 'none',
    transition: 'all var(--transition-fast)',
    cursor: 'pointer',
    appearance: 'none',
    backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%23999\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    backgroundSize: '16px',
  },
};

const emptyForm = {
  name: '',
  email: '',
  password: '',
  display_name: '',
  role: 'operator',
};

function UserFormModal({ user, onSubmit, onCancel, loading, error }) {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const isEditing = !!user;

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || '',
        email: user.email || '',
        password: '',
        display_name: user.display_name || '',
        role: user.role || 'operator',
      });
    } else {
      setForm(emptyForm);
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = 'Nome obbligatorio';
    if (!form.email.trim()) newErrors.email = 'Email obbligatoria';
    if (!isEditing && !form.password.trim()) newErrors.password = 'Password obbligatoria';
    if (form.password && form.password.length < 6) newErrors.password = 'Minimo 6 caratteri';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      display_name: form.display_name.trim() || null,
      role: form.role,
    };

    // Only include password if provided
    if (form.password) {
      payload.password = form.password;
    }

    onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit} style={styles.form}>
      {error && <div style={styles.error}>{error}</div>}

      <Input
        label="Nome"
        name="name"
        value={form.name}
        onChange={handleChange}
        error={errors.name}
        placeholder="Mario Rossi"
        required
      />

      <Input
        label="Email"
        name="email"
        type="email"
        value={form.email}
        onChange={handleChange}
        error={errors.email}
        placeholder="mario@esempio.it"
        required
      />

      <Input
        label={isEditing ? 'Password (lascia vuoto per non modificare)' : 'Password'}
        name="password"
        type="password"
        value={form.password}
        onChange={handleChange}
        error={errors.password}
        placeholder={isEditing ? '••••••••' : 'Minimo 6 caratteri'}
        required={!isEditing}
      />

      <Input
        label="Nome visualizzato"
        name="display_name"
        value={form.display_name}
        onChange={handleChange}
        placeholder="Nome mostrato in partita"
      />

      <div>
        <label style={styles.fieldLabel}>Ruolo</label>
        <select
          name="role"
          value={form.role}
          onChange={handleChange}
          style={styles.select}
          onFocus={(e) => {
            e.target.style.borderColor = 'var(--color-primary)';
            e.target.style.boxShadow = '0 0 0 3px rgba(212, 175, 55, 0.15)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = 'rgba(212, 175, 55, 0.15)';
            e.target.style.boxShadow = 'none';
          }}
        >
          <option value="operator">Organizzatore</option>
          <option value="admin">Amministratore</option>
        </select>
      </div>

      <div style={styles.formActions}>
        <Button variant="ghost" onClick={onCancel} type="button">
          <X size={16} />
          Annulla
        </Button>
        <Button variant="primary" type="submit" loading={loading}>
          <Save size={16} />
          {isEditing ? 'Salva' : 'Salva'}
        </Button>
      </div>
    </form>
  );
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [formError, setFormError] = useState(null);

  const loadUsers = async () => {
    try {
      const result = await getUsers();
      setUsers(Array.isArray(result) ? result : result.users || []);
    } catch {
      // handle silently
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleCreate = async (data) => {
    setSaving(true);
    setFormError(null);
    try {
      await createUser(data);
      await loadUsers();
      setShowForm(false);
    } catch (err) {
      setFormError(err.response?.data?.message || err.response?.data?.detail || 'Errore durante la creazione');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (data) => {
    if (!editUser) return;
    setSaving(true);
    setFormError(null);
    try {
      await updateUser(editUser.id, data);
      await loadUsers();
      setEditUser(null);
    } catch (err) {
      setFormError(err.response?.data?.message || err.response?.data?.detail || 'Errore durante la modifica');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (userId) => {
    try {
      await toggleUserActive(userId);
      await loadUsers();
    } catch {
      // handle silently
    }
  };

  const openCreate = () => {
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = (usr) => {
    setFormError(null);
    setEditUser(usr);
  };

  if (loading) {
    return (
      <div style={styles.centered}>
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Gestione organizzatori</h1>
          <p style={styles.subtitle}>Gestisci organizzatori e amministratori</p>
        </div>
        <Button variant="primary" onClick={openCreate}>
          <Plus size={16} />
          Nuovo organizzatore
        </Button>
      </div>

      <div style={styles.tableWrapper}>
        {users.length === 0 ? (
          <div style={styles.empty}>
            <Users size={48} style={{ color: 'var(--color-text-muted)', marginBottom: 'var(--space-md)', opacity: 0.3 }} />
            <p style={{ margin: 0 }}>Nessun organizzatore trovato. Clicca "Nuovo organizzatore" per iniziare.</p>
          </div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Nome</th>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>Ruolo</th>
                <th style={styles.th}>Nome visualizzato</th>
                <th style={styles.th}>Stato</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {users.map((usr) => (
                <tr
                  key={usr.id}
                  style={styles.row}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-bg-card)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <td style={{ ...styles.td, color: 'var(--color-text-primary)', fontWeight: 500 }}>
                    {usr.name}
                  </td>
                  <td style={{ ...styles.td, fontSize: '0.85rem' }}>
                    {usr.email}
                  </td>
                  <td style={styles.td}>
                    <Badge variant={usr.role === 'admin' ? 'gold' : 'info'}>
                      {usr.role === 'admin' ? 'Amministratore' : 'Organizzatore'}
                    </Badge>
                  </td>
                  <td style={styles.td}>
                    {usr.display_name || <span style={{ color: 'var(--color-text-muted)' }}>-</span>}
                  </td>
                  <td style={styles.td}>
                    <Badge variant={usr.is_active !== false ? 'success' : 'danger'}>
                      {usr.is_active !== false ? 'Attivo' : 'Non attivo'}
                    </Badge>
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 'var(--space-xs)', justifyContent: 'flex-end' }}>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(usr)}>
                        <Edit2 size={14} />
                        Modifica
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleActive(usr.id)}
                      >
                        <Power size={14} />
                        {usr.is_active !== false ? 'Disattiva' : 'Attiva'}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title="Nuovo organizzatore"
      >
        <UserFormModal
          onSubmit={handleCreate}
          onCancel={() => setShowForm(false)}
          loading={saving}
          error={formError}
        />
      </Modal>

      <Modal
        isOpen={!!editUser}
        onClose={() => setEditUser(null)}
        title="Modifica organizzatore"
      >
        <UserFormModal
          user={editUser}
          onSubmit={handleUpdate}
          onCancel={() => setEditUser(null)}
          loading={saving}
          error={formError}
        />
      </Modal>
    </div>
  );
}
